import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { TrendingUp, TrendingDown, Minus, Activity, Droplet, Scale } from 'lucide-react';
import { useVault } from '../contexts/VaultContext';

const HealthTrends = ({ familyMemberId = null }) => {
  const { api, isUnlocked } = useVault();
  const [trends, setTrends] = useState({
    bp: { data: [], trend: 'stable', message: '' },
    sugar: { data: [], trend: 'stable', message: '' },
    weight: { data: [], trend: 'stable', message: '' }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isUnlocked) {
      fetchTrends();
    }
  }, [familyMemberId, isUnlocked]);

  const fetchTrends = async () => {
    try {
      // Use encrypted API - data will be auto-decrypted
      const entries = await api.getDiaryEntries(familyMemberId) || [];
      
      // Process BP data
      const bpData = entries
        .filter(e => e.entry_type === 'blood_pressure')
        .sort((a, b) => new Date(a.entry_date) - new Date(b.entry_date))
        .slice(-7);
      
      // Process Sugar data
      const sugarData = entries
        .filter(e => e.entry_type === 'blood_sugar')
        .sort((a, b) => new Date(a.entry_date) - new Date(b.entry_date))
        .slice(-7);
      
      // Process Weight data
      const weightData = entries
        .filter(e => e.entry_type === 'weight')
        .sort((a, b) => new Date(a.entry_date) - new Date(b.entry_date))
        .slice(-7);
      
      setTrends({
        bp: analyzeTrend(bpData, 'bp'),
        sugar: analyzeTrend(sugarData, 'sugar'),
        weight: analyzeTrend(weightData, 'weight')
      });
    } catch (error) {
      console.error('Error fetching trends:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeTrend = (data, type) => {
    if (data.length < 2) {
      return { data, trend: 'insufficient', message: 'Need more data to show trends' };
    }
    
    let values = [];
    if (type === 'bp') {
      values = data.map(d => d.data?.systolic || 0);
    } else if (type === 'sugar') {
      values = data.map(d => d.data?.value || d.data?.level || 0);
    } else if (type === 'weight') {
      values = data.map(d => d.data?.value || 0);
    }
    
    const firstHalf = values.slice(0, Math.ceil(values.length / 2));
    const secondHalf = values.slice(Math.ceil(values.length / 2));
    
    const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
    
    const change = ((secondAvg - firstAvg) / firstAvg) * 100;
    const latestValue = values[values.length - 1];
    
    let trend = 'stable';
    let message = '';
    
    if (type === 'bp') {
      if (change < -3) {
        trend = 'improving';
        message = `Great job! Your BP is improving. Latest: ${latestValue}/${data[data.length-1]?.data?.diastolic}`;
      } else if (change > 5) {
        trend = 'concerning';
        message = `Your BP has been rising. Consider reducing salt and stress.`;
      } else {
        trend = 'stable';
        message = `BP is stable at ${latestValue}/${data[data.length-1]?.data?.diastolic}. Keep it up!`;
      }
    } else if (type === 'sugar') {
      if (change < -3) {
        trend = 'improving';
        message = `Excellent! Your sugar levels are coming down. Latest: ${latestValue} mg/dL`;
      } else if (change > 5) {
        trend = 'concerning';
        message = `Sugar levels trending up. Watch your carb intake.`;
      } else {
        trend = 'stable';
        message = `Sugar is stable at ${latestValue} mg/dL. Good control!`;
      }
    } else if (type === 'weight') {
      if (change < -1) {
        trend = 'improving';
        message = `You've lost weight! Now at ${latestValue} kg. Well done!`;
      } else if (change > 2) {
        trend = 'concerning';
        message = `Weight has increased to ${latestValue} kg. Time for more activity?`;
      } else {
        trend = 'stable';
        message = `Weight stable at ${latestValue} kg. Maintaining well!`;
      }
    }
    
    return { data, trend, message, latestValue };
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingDown className="h-4 w-4 text-green-500" />;
    if (trend === 'concerning') return <TrendingUp className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-blue-500" />;
  };

  const getTrendColor = (trend) => {
    if (trend === 'improving') return 'border-green-200 bg-green-50';
    if (trend === 'concerning') return 'border-red-200 bg-red-50';
    return 'border-blue-200 bg-blue-50';
  };

  const renderMiniChart = (data, type) => {
    if (!data || data.length < 2) return null;
    
    let values = [];
    if (type === 'bp') {
      values = data.map(d => d.data?.systolic || 0);
    } else if (type === 'sugar') {
      values = data.map(d => d.data?.value || d.data?.level || 0);
    } else if (type === 'weight') {
      values = data.map(d => d.data?.value || 0);
    }
    
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;
    
    return (
      <div className="flex items-end gap-1 h-10 mt-2">
        {values.map((val, i) => {
          const height = ((val - min) / range) * 100;
          const isLast = i === values.length - 1;
          return (
            <div
              key={i}
              className={`flex-1 rounded-t transition-all ${
                isLast ? 'bg-[#0D2340]' : 'bg-[#0D2340]/40'
              }`}
              style={{ height: `${Math.max(20, height)}%` }}
              title={`${val}`}
            />
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4 h-32 bg-slate-100" />
          </Card>
        ))}
      </div>
    );
  }

  // Show empty state if no data
  const hasAnyData = trends.bp.data.length >= 2 || trends.sugar.data.length >= 2 || trends.weight.data.length >= 2;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {/* Empty state when no trends available */}
      {!hasAnyData && (
        <Card className="sm:col-span-2 lg:col-span-3 border border-dashed border-slate-300 bg-slate-50" data-testid="health-trends-empty">
          <CardContent className="p-6 text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Activity className="h-6 w-6 text-slate-400" />
              <Droplet className="h-6 w-6 text-slate-400" />
              <Scale className="h-6 w-6 text-slate-400" />
            </div>
            <h3 className="font-semibold text-slate-700 mb-1">No Health Trends Yet</h3>
            <p className="text-sm text-slate-500">
              Start logging your BP, blood sugar, or weight in the Diary to see trends here.
            </p>
          </CardContent>
        </Card>
      )}

      {/* BP Trend */}
      {trends.bp.data.length >= 2 && (
        <Card className={`border ${getTrendColor(trends.bp.trend)}`} data-testid="health-trend-bp">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-red-500" />
                <span className="font-semibold text-sm">Blood Pressure</span>
              </div>
              {getTrendIcon(trends.bp.trend)}
            </div>
            {renderMiniChart(trends.bp.data, 'bp')}
            <p className="text-xs text-slate-600 mt-2 leading-relaxed">
              {trends.bp.message}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Sugar Trend */}
      {trends.sugar.data.length >= 2 && (
        <Card className={`border ${getTrendColor(trends.sugar.trend)}`} data-testid="health-trend-sugar">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Droplet className="h-5 w-5 text-purple-500" />
                <span className="font-semibold text-sm">Blood Sugar</span>
              </div>
              {getTrendIcon(trends.sugar.trend)}
            </div>
            {renderMiniChart(trends.sugar.data, 'sugar')}
            <p className="text-xs text-slate-600 mt-2 leading-relaxed">
              {trends.sugar.message}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Weight Trend */}
      {trends.weight.data.length >= 2 && (
        <Card className={`border ${getTrendColor(trends.weight.trend)}`} data-testid="health-trend-weight">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Scale className="h-5 w-5 text-blue-500" />
                <span className="font-semibold text-sm">Weight</span>
              </div>
              {getTrendIcon(trends.weight.trend)}
            </div>
            {renderMiniChart(trends.weight.data, 'weight')}
            <p className="text-xs text-slate-600 mt-2 leading-relaxed">
              {trends.weight.message}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HealthTrends;
